import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';

import { useAuth } from '../../context/authContext';
import useAvailabilityDiagnostics from '../../hooks/useAvailabilityDiagnostics';
import { attentionStripeBackground } from '../../util/attentionStripes';
import { formatDate } from '../../util/formatHelper';

const REQUIRED_RIGHT = 'dev:tool:availabilityDiagnostics';

// Verdict → display label + MUI palette colour. The "error" verdicts are the
// suspected measurement errors we want to surface with attention stripes.
const VERDICT_META = {
  measurement_error: { label: 'Vermutlich Messfehler', color: 'error', attention: true },
  genuinely_empty: { label: 'Tatsächlich leer', color: 'success', attention: false },
  no_response: { label: 'Keine Antwort', color: 'warning', attention: true },
  no_probe: { label: 'Kein Request erfasst', color: 'warning', attention: true },
  unknown: { label: 'Unbekannt', color: 'default', attention: false },
};

const verdictMeta = verdict => VERDICT_META[verdict] || VERDICT_META.unknown;

function AvailabilityDiagnosticsView() {
  const authCtx = useAuth();
  const theme = useTheme();
  const { data, isLoading, fetchDiagnostics } = useAvailabilityDiagnostics();

  const [widgetId, setWidgetId] = useState('');
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

  const hasRight = authCtx.checkRight(REQUIRED_RIGHT);

  const load = useCallback(() => {
    if (!hasRight) return;
    fetchDiagnostics({ widgetId, startDate, endDate, limit: 200 }).catch(() => {});
  }, [hasRight, fetchDiagnostics, widgetId, startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  const items = data?.items || [];
  const widgets = data?.widgets || [];
  const verdictCounts = data?.verdictCounts || {};

  const measurementErrors = verdictCounts.measurement_error || 0;
  const totalShown = items.length;
  const errorShare = useMemo(() => {
    const totalVerdicts = Object.values(verdictCounts).reduce((a, b) => a + b, 0);
    return totalVerdicts > 0 ? Math.round((measurementErrors / totalVerdicts) * 100) : 0;
  }, [verdictCounts, measurementErrors]);

  // Rights gate AFTER all hooks (rules-of-hooks safe).
  if (!hasRight) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 1 }}>
        <ScienceOutlinedIcon color='primary' />
        <Typography variant='h4' sx={{ fontWeight: 700 }}>
          Verfügbarkeits-Diagnostik
        </Typography>
        <Chip size='small' label='dev' color='secondary' variant='outlined' />
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Pro Session, die „Keine verfügbaren Uhrzeiten" gesehen hat, ein Eintrag mit der exakten
        Anfrage und Antwort des Browsers. Dient der manuellen Prüfung des vermuteten Messfehlers
        hinter der hohen Leere-Verfügbarkeit-Quote. Rot schraffierte Einträge sind verdächtig.
      </Typography>

      {/* Filter bar */}
      <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
          <TextField
            select
            label='Booking-Widget'
            value={widgetId}
            onChange={e => setWidgetId(e.target.value)}
            sx={{ minWidth: 240 }}
            size='small'
          >
            <MenuItem value=''>— Alle Widgets —</MenuItem>
            {widgets.map(w => (
              <MenuItem key={w.id} value={w.id}>
                {w.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type='date'
            label='Von'
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            size='small'
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type='date'
            label='Bis'
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            size='small'
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant='contained'
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={isLoading}
          >
            Aktualisieren
          </Button>
        </Stack>
      </Paper>

      {/* Verdict summary */}
      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{ mb: 2 }}>
        {Object.entries(verdictCounts).length === 0 ? (
          <Chip label='Keine Einträge im Zeitraum' variant='outlined' />
        ) : (
          Object.entries(verdictCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([verdict, count]) => {
              const meta = verdictMeta(verdict);
              return (
                <Chip
                  key={verdict}
                  label={`${meta.label}: ${count}`}
                  color={meta.color === 'default' ? undefined : meta.color}
                  variant={meta.attention ? 'filled' : 'outlined'}
                />
              );
            })
        )}
      </Stack>

      {measurementErrors > 0 && (
        <Alert severity='error' icon={<ScienceOutlinedIcon />} sx={{ mb: 2 }}>
          <strong>{measurementErrors}</strong> Eintrag/Einträge ({errorShare}% der erfassten) sind
          vermutlich ein Messfehler — die Antwort enthielt buchbare oder „auf Anfrage"-Slots, die
          UI hat sie dennoch als leer gewertet. Details unten je Eintrag.
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && totalShown === 0 && (
        <Alert severity='info'>
          Keine Diagnose-Einträge im gewählten Zeitraum. Die Erfassung beginnt mit dem nächsten
          Deployment des Booking-Widgets — vorhandene Sessions tragen nur das alte Boolean-Flag.
        </Alert>
      )}

      {/* Records */}
      <Stack spacing={1.25}>
        {items.map(item => {
          const meta = verdictMeta(item.verdict);
          const resp = item?.diagnostic?.response || {};
          const availCount = resp?.availableStartTimesCount ?? '—';
          const reqOnlyCount = resp?.requestOnlyStartTimesCount ?? '—';
          const summaryBg = meta.attention
            ? attentionStripeBackground(theme, { color: meta.color })
            : undefined;

          return (
            <Accordion key={item._id} disableGutters variant='outlined'>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ background: summaryBg }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                  sx={{ width: '100%', pr: 1 }}
                >
                  <Chip
                    size='small'
                    label={meta.label}
                    color={meta.color === 'default' ? undefined : meta.color}
                  />
                  <Typography variant='body2' sx={{ fontWeight: 600, minWidth: 120 }}>
                    {formatDate(item.serverTs)}
                  </Typography>
                  <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }}>
                    {item.widgetName || item.widgetId}
                    {item.date ? ` · Wunschtag ${item.date}` : ''}
                  </Typography>
                  <Chip
                    size='small'
                    variant='outlined'
                    label={`buchbar: ${availCount} · auf Anfrage: ${reqOnlyCount}`}
                  />
                  {item?.context?.deviceType && (
                    <Chip size='small' variant='outlined' label={item.context.deviceType} />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {item?.diagnostic?.verdictReason && (
                  <Typography variant='body2' sx={{ mb: 1 }}>
                    <strong>Einschätzung:</strong> {item.diagnostic.verdictReason}
                  </Typography>
                )}
                <Divider sx={{ mb: 1 }} />
                <Typography variant='caption' color='text.secondary'>
                  Vollständiger Datensatz (Anfrage + Antwort + Client-State)
                </Typography>
                <Box
                  component='pre'
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'grey.900',
                    color: 'grey.100',
                    fontSize: 12,
                    lineHeight: 1.5,
                    overflow: 'auto',
                    maxHeight: 480,
                  }}
                >
                  {JSON.stringify(item, null, 2)}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
}

export default AvailabilityDiagnosticsView;
